/**
 * "Change data scope" — the Data tab's one control, using the same editor as
 * New audit step 2 so a scope is changed exactly the way it was made.
 *
 * Saving re-locks the audit's rows; every analysis result computed before is
 * then out of date until re-run, and the dialog says so before anything moves.
 */
import { useModule } from '../modules/useModule';
import { useEffect, useState } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { Dialog, ErrorNotice, Note } from '../../shared/ui';
import {
	DataScopeEditor,
	toScope,
	useScopeCount,
} from '../audits/DataScopeEditor';
import { useClientFilesQuery } from '../../queries/clients';
import { isIncluded } from '../../utils/files';
import type { CanonicalRowFilter } from '../../schemas';

export function ChangeDataScopeDialog({
	open,
	clientId,
	clientName,
	initial,
	hasResults,
	pending,
	error,
	onCancel,
	onConfirm,
}: {
	open: boolean;
	clientId: string;
	clientName: string;
	/** The audit's stored filter. */
	initial: unknown;
	/** True when any analysis has already produced a result. */
	hasResults: boolean;
	pending?: boolean;
	error?: unknown;
	onCancel: () => void;
	onConfirm: (filter: CanonicalRowFilter) => void;
}) {
	const { module } = useModule();
	const library = useClientFilesQuery(clientId, module, open);
	const [scope, setScope] = useState<CanonicalRowFilter>({});
	/* The scope is filled once per opening, as soon as the client's files are
     known — not again on a refetch, which would undo the person's edits. */
	const [seeded, setSeeded] = useState(false);
	useEffect(() => {
		if (!open) setSeeded(false);
	}, [open]);
	useEffect(() => {
		if (!open || seeded || !library.data) return;
		const stored = toScope(initial);
		/* Saved before files had to be chosen: no files then meant every
       processed file, so open with all of them ticked and the real count. */
		if (!stored.fileIds?.length) {
			const all = library.data
				.filter(isIncluded)
				.map((file) => file.pkFileId);
			if (all.length) stored.fileIds = all;
		}
		setScope(stored);
		setSeeded(true);
	}, [open, seeded, initial, library.data]);
	const count = useScopeCount(clientId, scope, open && seeded);

	return (
		<Dialog
			open={open}
			onClose={onCancel}
			title="Change data scope"
			subtitle={clientName}
			width={880}
			plainFooter
			footer={
				<Box
					sx={{
						display: 'flex',
						gap: 'var(--s2)',
						justifyContent: 'flex-end',
					}}
				>
					<Button
						variant="outlined"
						onClick={onCancel}
						disabled={pending}
					>
						Cancel
					</Button>
					<Button
						loading={pending}
						variant="contained"
						disabled={pending || !count.ready}
						title={
							count.ready
								? undefined
								: 'Choose at least one file, and make sure at least one row is in scope.'
						}
						onClick={() => onConfirm(scope)}
					>
						Save data scope
					</Button>
				</Box>
			}
		>
			<div className="stack">
				{hasResults && (
					<Note
						tone="warn"
						row
						title="Existing results will be out of date"
					>
						Saving re-locks this audit's rows. Analysis results
						produced before the change stay visible but are marked
						out of date until you re-run the analyses.
					</Note>
				)}
				<ErrorNotice error={error ?? null} />
				<ErrorNotice error={library.error} />
				{open &&
					(seeded ? (
						<DataScopeEditor
							clientId={clientId}
							clientName={clientName}
							value={scope}
							onChange={setScope}
						/>
					) : (
						!library.error && (
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'center',
									p: 'var(--s6)',
								}}
							>
								<CircularProgress
									size={24}
									aria-label="Loading"
								/>
							</Box>
						)
					))}
			</div>
		</Dialog>
	);
}
